import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Central resolver for role display names.
 *
 * Employee records store the immutable role KEY (e.g. `team_lead`).
 * The friendly label lives in `master_roles.name` and can be renamed at any time
 * from Masters → Roles. Every UI that shows a role to a human MUST look up the
 * current name via this hook — never render `user.role` as text directly.
 *
 * Falls back to a title-cased version of the key if the catalog hasn't loaded
 * or the key is missing (e.g. legacy `team_lead` → "Team Lead"). Silent fallback
 * — never blocks the render.
 */
const RoleCatalogContext = createContext({ roles: [], byKey: {}, reload: async () => {} });

export function RoleCatalogProvider({ children }) {
  const { user } = useAuth();          // only fetch when authed (endpoint requires JWT)
  const [roles, setRoles] = useState([]);

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/masters/roles");
      const rows = Array.isArray(r.data) ? r.data : (r.data?.rows || r.data?.items || []);
      setRoles(rows);
    } catch { /* silent — display falls back to prettified key */ }
  }, []);

  useEffect(() => { if (user) reload(); }, [user, reload]);

  const byKey = Object.fromEntries(roles.map((r) => [r.key, r.name]));
  return <RoleCatalogContext.Provider value={{ roles, byKey, reload }}>{children}</RoleCatalogContext.Provider>;
}

const _prettify = (key) => (key || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/** Hook: returns a `roleName(key) → string` resolver. */
export function useRoleName() {
  const { byKey } = useContext(RoleCatalogContext);
  return (key) => byKey[key] || _prettify(key);
}

/** Hook: returns the full role catalog + resolver for advanced use. */
export function useRoleCatalog() {
  return useContext(RoleCatalogContext);
}

/** Component: <RoleName role="team_lead" /> — renders the current display name. */
export function RoleName({ role, fallback, className, testid }) {
  const roleName = useRoleName();
  const label = role ? roleName(role) : (fallback || "");
  return <span className={className} data-testid={testid}>{label}</span>;
}
